import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import adminService from "../../../services/admin.service";
import ownerService from "../../../services/owner.service";
import { IAdmin, ICreateAdminRequest, IUpdateAdminRequest, IStaffRole } from "../../../types/admin.types";
import { IOwnerObject } from "../../../services/api.types";
import { STATUS_SUCCESS } from "../../../constants/responseTypes";
import { logger } from "../../../utils/logger";

const USERNAME_REGEX = /^[a-zA-Z][a-zA-Z0-9]{2,}$/;

const AdminManagement = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState<IAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<IAdmin | null>(null);
  const [roles, setRoles] = useState<IStaffRole[]>([]);
  const [formData, setFormData] = useState<ICreateAdminRequest>({
    username: "",
    password: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    country_code: "+994",
    role: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [ownerCount, setOwnerCount] = useState<number | null>(null);

  useEffect(() => {
    fetchAdmins();
    // Mount-only fetch; fetchAdmins is a plain (non-memoized) function
    // recreated every render, so adding it here would refetch on every
    // render instead of once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    adminService.listRoles().then(setRoles).catch(() => {});
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getAllAdmins();
      setAdmins(data);
      logger.info("Fetched admins", data.length);
    } catch (err: any) {
      setError(err.message || t("admins.fetchFailed"));
      logger.error("Failed to fetch admins", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!USERNAME_REGEX.test(formData.username)) {
        setError(t("admins.usernameRule"));
        return;
      }
      setFormLoading(true);
      await adminService.createAdmin(formData);
      await fetchAdmins();
      setCreateDialogOpen(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || t("admins.createFailed"));
      logger.error("Failed to create admin", err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedAdmin) return;
    
    try {
      if (!USERNAME_REGEX.test(formData.username)) {
        setError(t("admins.usernameRule"));
        return;
      }
      // Password is optional on edit (blank = keep current), but if provided it
      // must clear the same 8-char floor the backend's changeAdminPassword
      // enforces -- checked here so a too-short value fails before either
      // request fires, rather than updating the profile and then 400ing on
      // the password half.
      if (formData.password && formData.password.length < 8) {
        setError(t("rm.passwordTooShort"));
        return;
      }
      setFormLoading(true);
      const updateData: IUpdateAdminRequest = {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        country_code: formData.country_code,
        role: formData.role,
      };
      await adminService.updateAdmin(selectedAdmin._id, updateData);
      if (formData.password) {
        // currentPassword is required (non-empty) by the endpoint but never
        // verified for a super_admin changing someone ELSE's password (only
        // checked when admin._id === the caller's own id) -- see
        // changeAdminPassword in adminController.js. newPassword is sent for
        // both fields rather than inventing a meaningless placeholder value.
        await adminService.changePassword(selectedAdmin._id, {
          currentPassword: formData.password,
          newPassword: formData.password,
          confirmPassword: formData.password,
        });
      }
      await fetchAdmins();
      setEditDialogOpen(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || t("admins.updateFailed"));
      logger.error("Failed to update admin", err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;
    
    try {
      setFormLoading(true);
      await adminService.deleteAdmin(selectedAdmin._id);
      await fetchAdmins();
      setDeleteDialogOpen(false);
      setSelectedAdmin(null);
    } catch (err: any) {
      setError(err.message || t("admins.deleteFailed"));
      logger.error("Failed to delete admin", err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (admin: IAdmin) => {
    try {
      await adminService.toggleAdminStatus(admin._id, !admin.active);
      await fetchAdmins();
    } catch (err: any) {
      setError(err.message || t("admins.statusUpdateFailed"));
      logger.error("Failed to update admin status", err);
    }
  };

  const openEditDialog = (admin: IAdmin) => {
    setSelectedAdmin(admin);
    setFormData({
      username: admin.username,
      password: "", // Don't show existing password
      email: admin.email || "",
      firstName: admin.firstName || "",
      lastName: admin.lastName || "",
      phone: admin.phone || "",
      country_code: admin.country_code || "+994",
      role: admin.role?._id ?? "",
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (admin: IAdmin) => {
    setSelectedAdmin(admin);
    setOwnerCount(null);
    setDeleteDialogOpen(true);

    // Fetch owner count if admin is a relationship manager
    if (admin.role?.name === "relationship_manager") {
      (async () => {
        try {
          const ownersRes = await ownerService.getOwners();
          const allOwners: IOwnerObject[] = ownersRes.status === STATUS_SUCCESS ? ownersRes.data : [];
          const rmOwners = allOwners.filter((o) => o.relationshipManager?._id === admin._id);
          setOwnerCount(rmOwners.length);
        } catch (err: any) {
          logger.error("Failed to fetch owner count for RM delete", err);
          // Do not set error - let the user proceed with delete
        }
      })();
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      country_code: "+994",
      role: "",
    });
    setSelectedAdmin(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="p-4">
      <Paper className="p-6">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">{t("admins.title")}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t("admins.addAdmin")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("admins.colUsername")}</TableCell>
                <TableCell>{t("common.name")}</TableCell>
                <TableCell>{t("ownerInfo.email")}</TableCell>
                <TableCell>{t("common.phone")}</TableCell>
                <TableCell>{t("admins.colRole")}</TableCell>
                <TableCell>{t("common.status")}</TableCell>
                <TableCell>{t("admins.colLastLogin")}</TableCell>
                <TableCell align="center">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin._id} hover>
                  <TableCell>{admin.username}</TableCell>
                  <TableCell>
                    {admin.firstName && admin.lastName
                      ? `${admin.firstName} ${admin.lastName}`
                      : t("common.notAvailable")}
                  </TableCell>
                  <TableCell>{admin.email || t("common.notAvailable")}</TableCell>
                  <TableCell>
                    {admin.phone
                      ? `${admin.country_code} ${admin.phone}`
                      : t("common.notAvailable")}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        admin.role?.label ??
                        admin.role?.name ??
                        t("common.unknown")
                      }
                      color={admin.role?.name === "super_admin" ? "error" : "primary"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        admin.active ? t("status.active") : t("status.inactive")
                      }
                      color={admin.active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {admin.lastLogin
                      ? new Date(admin.lastLogin).toLocaleDateString()
                      : t("common.never")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("admins.editAdmin")}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(admin)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        admin.active
                          ? t("admins.deactivate")
                          : t("admins.activate")
                      }
                    >
                      <IconButton
                        size="small"
                        color={admin.active ? "warning" : "success"}
                        onClick={() => handleToggleStatus(admin)}
                      >
                        {admin.active ? (
                          <LockIcon fontSize="small" />
                        ) : (
                          <LockOpenIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("admins.deleteAdmin")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(admin)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admins.createAdmin")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t("admins.username")}
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            margin="dense"
            required
            helperText={t("admins.usernameHelp")}
          />
          <TextField
            fullWidth
            label={t("admins.password")}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="dense"
            required
          />
          <TextField
            fullWidth
            label={t("ownerInfo.email")}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label={t("common.firstName")}
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label={t("common.lastName")}
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            margin="dense"
          />
          <Box display="flex" gap={1}>
            <TextField
              label={t("common.countryCode")}
              value={formData.country_code}
              onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
              margin="dense"
              style={{ width: "150px" }}
            />
            <TextField
              fullWidth
              label={t("common.phone")}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              margin="dense"
            />
          </Box>
          <TextField
            fullWidth
            select
            label={t("admins.colRole")}
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            margin="dense"
          >
            {roles.map((r) => (
              <MenuItem key={r._id} value={r._id}>{r.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); resetForm(); }}>{t("common.cancel")}</Button>
          <Button onClick={handleCreate} variant="contained" disabled={formLoading || !formData.username || !formData.password}>
            {formLoading ? <CircularProgress size={24} /> : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admins.editAdmin")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t("admins.username")}
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            margin="dense"
            required
            helperText={t("admins.usernameHelp")}
          />
          <TextField
            fullWidth
            type="password"
            label={t("rm.newPassword")}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="dense"
            helperText={t("admins.passwordOptionalHelp")}
          />
          <TextField
            fullWidth
            label={t("ownerInfo.email")}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label={t("common.firstName")}
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label={t("common.lastName")}
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            margin="dense"
          />
          <Box display="flex" gap={1}>
            <TextField
              label={t("common.countryCode")}
              value={formData.country_code}
              onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
              margin="dense"
              style={{ width: "150px" }}
            />
            <TextField
              fullWidth
              label={t("common.phone")}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              margin="dense"
            />
          </Box>
          <TextField
            fullWidth
            select
            label={t("admins.colRole")}
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            margin="dense"
          >
            {roles.map((r) => (
              <MenuItem key={r._id} value={r._id}>{r.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); resetForm(); }}>{t("common.cancel")}</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={formLoading || !formData.username}>
            {formLoading ? <CircularProgress size={24} /> : t("common.update")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t("admins.deleteAdmin")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("admins.deleteConfirm", {
              username: selectedAdmin?.username ?? "",
            })}
          </Typography>
          {ownerCount !== null && (
            <Typography
              color="warning.main"
              sx={{ mt: 2 }}
            >
              {t("admins.deleteReassignWarning", {
                count: ownerCount,
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={formLoading}>
            {formLoading ? <CircularProgress size={24} /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
